import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import Dropdown from "../ui/Dropdown";

const AssignCommissionerModal = ({
  isOpen,
  onClose,
  user,
  onAssignCommissioner,
}) => {
  const [selectedLeague, setSelectedLeague] = useState("");
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchAvailableLeagues = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/leagues`);
      const allLeagues = response.data.leagues || [];

      // Filter out leagues where this user is already commissioner
      const availableLeagues = allLeagues.filter(
        (league) => league.commissionerId !== user?.id,
      );

      const leagueOptions = availableLeagues.map((league) => ({
        value: league.id,
        label: `${league.name} (Current: ${league.commissionerName || "None"})`,
      }));

      setLeagues(leagueOptions);
    } catch (err) {
      setError("Failed to fetch leagues");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchAvailableLeagues();
  }, [fetchAvailableLeagues]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLeague) {
      setError("Please select a league");
      return;
    }

    setLoading(true);
    try {
      await onAssignCommissioner(user.id, selectedLeague);
      setSelectedLeague("");
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.error?.message || "Failed to assign commissioner",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedLeague("");
    setError("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Assign Commissioner">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Assign <strong>{user?.name}</strong> as commissioner to another
            league
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div>
          <Dropdown
            label="Select League"
            value={selectedLeague}
            onChange={setSelectedLeague}
            options={leagues}
            placeholder="Choose a league..."
          />
        </div>

        {leagues.length === 0 && !loading && (
          <div className="text-sm text-gray-500 text-center py-4">
            No available leagues to assign. User is already commissioner of all
            leagues.
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading || !selectedLeague || leagues.length === 0}
          >
            {loading ? "Assigning..." : "Assign Commissioner"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AssignCommissionerModal;
